import React, { useCallback, useEffect, useMemo, useRef, memo } from 'react';
import { Group } from 'react-konva';
import { getConnectorManifest, type DiagramType } from '../../../lib/diagram/registries';
import { EdgeConnector } from '../EdgeConnector';
import { Edge, Node } from '../../../core/models/scene';
import * as awarenessProtocol from 'y-protocols/awareness';
import VersionedComponentRegistry from '../../../lib/diagram/VersionedComponentRegistry';
import { useCompassViewPrefs } from '../../../stores/compassViewPrefsStore';
import { useViewRulesStore } from '../../../core/assistant/stores/viewRulesStore';
import {
  getActiveFilterRules,
  getEffectiveFilterEffect,
  getEffectiveShowReportingLines,
  getRuleKind,
} from '../../../core/assistant/models/ViewRules';
import { useOrgChartViewStore } from '../../../stores/orgchartViewStore';
import {
  resolveSecondaryReportsColor,
} from '../../../domains/orgchart/services/secondaryReportsColor';
import {
  DERIVED_SUCCESSOR_EDGE_PREFIX,
  deriveSuccessionLensEdges,
  resolveSuccessionLensEdge,
} from '../../../domains/orgchart/services/successionLensEdges';
import { DEFAULT_ORG_SECONDARY_REPORTS_VISIBILITY_MODE } from '../../../domains/orgchart/models/secondaryReportsVisibility';
import { useLabelSettingsStore } from '../../../stores/labelSettingsStore';
import type { PersonLabelGeometryContext } from '../../../lib/diagram/personLabelBounds';

const isOrgSecondaryReportsEdge = (diagramType: DiagramType, edge: Edge): boolean => {
  if (diagramType !== 'org') return false;
  const edgeType = String(edge.type ?? '');
  const relation = String((edge.attrs as Record<string, unknown> | undefined)?.relation ?? '');
  if (edgeType === 'dotted' || edgeType === 'matrix') return true;
  return (
    relation === 'dotted' ||
    relation === 'matrix' ||
    relation === 'dotted_line_reports' ||
    relation === 'matrix_reports'
  );
};

interface EdgesRendererProps {
  edges: Edge[];
  nodes: Node[];
  diagramType?: DiagramType;
  selectedIds: Set<string>;
  getShapeState?: <T extends Record<string, any>>(shapeId: string, baseState: T) => T;
  awareness?: awarenessProtocol.Awareness | null;
  registerEdgeRef: (edgeId: string, node: any | null) => void;
  versions?: Map<string, number>; // per-edge versions for fine-grained re-render
  onEdgeClick: (edgeId: string, e: any) => void;
  onStartEdgeTextEdit?: (edgeId: string, textNode: any) => void;
  editingEdgeText?: string;
  editRequestEdgeId?: string | null;
  canWrite?: boolean;
}

interface EdgeViewRuleState {
  opacity: number;
  visible: boolean;
}

const DEFAULT_NETWORK_IMPORT_MAX_VISIBLE_EDGE_DISTANCE = 520;

function nodeCenter(node: Node | undefined): { x: number; y: number } | null {
  if (!node) return null;
  const attrs = ((node as any).attrs ?? {}) as Record<string, unknown>;
  const width = pickNumber((node as any).rectW, (node as any).width, node.width, node.w, attrs.rectW, attrs.width, attrs.w) ?? 180;
  const height = pickNumber((node as any).rectH, (node as any).height, node.height, node.h, attrs.rectH, attrs.height, attrs.h) ?? 60;
  const x = pickNumber((node as any).rectX, attrs.rectX, node.x) ?? node.x;
  const y = pickNumber((node as any).rectY, attrs.rectY, node.y) ?? node.y;
  return { x: x + width / 2, y: y + height / 2 };
}

function pickNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
}

function importedEdgeDistance(fromNode: Node | undefined, toNode: Node | undefined): number {
  const from = nodeCenter(fromNode);
  const to = nodeCenter(toNode);
  if (!from || !to) return 0;
  return Math.abs(from.x - to.x) + Math.abs(from.y - to.y);
}

function isImportedNetworkEdge(edgeAttrs: any): boolean {
  return Boolean(edgeAttrs?.importedFrom || edgeAttrs?.infraEdgeKind || edgeAttrs?.networkImportEdge);
}

function isCompassExplorerEdge(edgeAttrs: any): boolean {
  return edgeAttrs?.layoutSource === 'compass-explorer' || edgeAttrs?.importedFrom === 'compass-explorer';
}

const areEdgesRendererPropsEqual = (prev: EdgesRendererProps, next: EdgesRendererProps): boolean =>
  prev.edges === next.edges &&
  prev.nodes === next.nodes &&
  prev.diagramType === next.diagramType &&
  prev.selectedIds === next.selectedIds &&
  prev.awareness === next.awareness &&
  prev.versions === next.versions &&
  prev.editingEdgeText === next.editingEdgeText &&
  prev.editRequestEdgeId === next.editRequestEdgeId &&
  prev.canWrite === next.canWrite;

// Memoized wrapper for konva-native connector rendering
interface KonvaNativeEdgeProps {
  manifest: ReturnType<typeof getConnectorManifest>;
  edge: Edge;
  fromNode?: Node;
  toNode?: Node;
  selected: boolean;
  getShapeState?: <T extends Record<string, any>>(shapeId: string, baseState: T) => T;
  awareness?: awarenessProtocol.Awareness | null;
  registerRef: (id: string, n: any | null) => void;
  version: number;
  onClick: (e: any) => void;
  editingEdgeText?: string;
  labelGeometry: PersonLabelGeometryContext;
}

type KonvaNativeManifest = NonNullable<ReturnType<typeof getConnectorManifest>> & {
  konvaNative: NonNullable<NonNullable<ReturnType<typeof getConnectorManifest>>['konvaNative']>;
};

type ActiveKonvaNativeEdgeProps = Omit<KonvaNativeEdgeProps, 'manifest' | 'fromNode' | 'toNode'> & {
  manifest: KonvaNativeManifest;
  fromNode: Node;
  toNode: Node;
};

const KonvaNativeEdgeBody = memo(function KonvaNativeEdgeBody({
  manifest,
  edge,
  fromNode,
  toNode,
  selected,
  getShapeState,
  awareness,
  registerRef,
  version,
  onClick,
  editingEdgeText,
  labelGeometry,
}: ActiveKonvaNativeEdgeProps) {
  // Recompute connector endpoints when awareness previews change for connected nodes
  const [awarenessVersion, setAwarenessVersion] = React.useState(0);
  React.useEffect(() => {
    if (!awareness || !fromNode || !toNode) return;
    const onChange = () => {
      try {
        const states = awareness.getStates();
        const hasRelevantPreview = Array.from(states.values()).some((state: any) => {
          const previews = state?.previews;
          if (!previews || typeof previews !== 'object') return false;
          const fromNodePreview = previews[fromNode.id];
          const toNodePreview = previews[toNode.id];
          const hasFrom = !!(fromNodePreview && fromNodePreview.props && (typeof fromNodePreview.props.x === 'number' || typeof fromNodePreview.props.y === 'number'));
          const hasTo = !!(toNodePreview && toNodePreview.props && (typeof toNodePreview.props.x === 'number' || typeof toNodePreview.props.y === 'number'));
          return hasFrom || hasTo;
        });
        if (hasRelevantPreview) setAwarenessVersion((v) => v + 1);
      } catch {
        // ignore malformed awareness state
      }
    };
    awareness.on('change', onChange);
    return () => { awareness.off('change', onChange); };
  }, [awareness, fromNode?.id, toNode?.id]);

  const computedVersion = (version ?? 0) + awarenessVersion;

  return (
    <>
      {manifest.konvaNative({
        edge,
        fromNode,
        toNode,
        props: (edge.attrs || {}) as any,
        selected,
        getShapeState: (id: string, base: any) => (getShapeState ? getShapeState(id, base) : base),
        awareness,
        registerRef,
        version: computedVersion,
        labelGeometry,
        handlers: { onClick, editingText: editingEdgeText === edge.id },
      })}
    </>
  );
}); // Use default memo - does shallow comparison of all props

const KonvaNativeEdge = memo(function KonvaNativeEdge(props: KonvaNativeEdgeProps) {
  if (!props.manifest?.konvaNative) return null;

  // Don't render if connected nodes are missing (they might be deleted)
  if (!props.fromNode || !props.toNode) return null;

  return (
    <KonvaNativeEdgeBody
      {...props}
      manifest={props.manifest as KonvaNativeManifest}
      fromNode={props.fromNode}
      toNode={props.toNode}
    />
  );
}); // Use default memo - does shallow comparison of all props

export const EdgesRenderer: React.FC<EdgesRendererProps> = React.memo((props) => {
  const {
    edges,
    nodes,
    diagramType = 'genogram',
    selectedIds,
    getShapeState,
    awareness,
    registerEdgeRef,
    versions,
    onEdgeClick,
    onStartEdgeTextEdit,
    editingEdgeText,
    editRequestEdgeId,
    canWrite = true,
  } = props;
  const line2Field = useLabelSettingsStore((state) => state.line2Field);
  const labelGeometry = useMemo<PersonLabelGeometryContext>(
    () => ({ line2Field }),
    [line2Field],
  );
  const rules = useViewRulesStore((s) => s.rules);
  const secondaryReportsVisibilityMode = useOrgChartViewStore(
    (s) => s.secondaryReportsVisibilityMode,
  );
  const matchingNodeIds = useViewRulesStore((s) => s.matchingNodeIds);
  const reportingChainNodeIds = useViewRulesStore((s) => s.reportingChainNodeIds);
  const reportingChainEdgeIds = useViewRulesStore((s) => s.reportingChainEdgeIds);
  // Keep latest register function without recreating per-edge ref callbacks
  const registerRefLatest = useRef(registerEdgeRef);
  useEffect(() => { registerRefLatest.current = registerEdgeRef; }, [registerEdgeRef]);

  // Stable ref callbacks per edge id
  const edgeRefCbMap = useRef(new Map<string, (n: any | null) => void>());
  const getStableEdgeRef = useCallback((id: string) => {
    const existing = edgeRefCbMap.current.get(id);
    if (existing) return existing;
    const cb = (n: any | null) => { registerRefLatest.current(id, n); };
    edgeRefCbMap.current.set(id, cb);
    return cb;
  }, []);

  // Stable onClick callbacks per edge id
  const clickCbMap = useRef(new Map<string, (e: any) => void>());
  const getStableOnClick = useCallback((id: string) => {
    const existing = clickCbMap.current.get(id);
    if (existing) return existing;
    const cb = (e: any) => {
      if (e) e.cancelBubble = true;
      if (e?.evt?.stopPropagation) e.evt.stopPropagation();
      onEdgeClick(id, e);
    };
    clickCbMap.current.set(id, cb);
    return cb;
  }, [onEdgeClick]);
  const edgeTierFilter = useCompassViewPrefs((s) => s.edgeTierFilter);

  const edgeViewRuleStateById = useMemo(() => {
    const map = new Map<string, EdgeViewRuleState>();
    const filterMode = getEffectiveFilterEffect(rules);
    const isFilterActive = getActiveFilterRules(rules).length > 0;
    const showReportingLines = getEffectiveShowReportingLines(rules);
    const nodeById = new Map(nodes.map((node) => [node.id, node] as const));

    for (const edge of edges) {
      const fromNode = nodeById.get(edge.from);
      const toNode = nodeById.get(edge.to);
      const fromMatches = fromNode ? matchingNodeIds.has(fromNode.id) : true;
      const toMatches = toNode ? matchingNodeIds.has(toNode.id) : true;
      const fromInChain = fromNode ? reportingChainNodeIds.has(fromNode.id) : false;
      const toInChain = toNode ? reportingChainNodeIds.has(toNode.id) : false;
      const edgeInChain = reportingChainEdgeIds.has(edge.id);
      const fromVisibleInHideMode = fromMatches || (showReportingLines && fromInChain);
      const toVisibleInHideMode = toMatches || (showReportingLines && toInChain);
      const visible = !(isFilterActive && filterMode === 'hide' && (!fromVisibleInHideMode || !toVisibleInHideMode));
      const edgeDimmed = isFilterActive && !fromMatches && !toMatches;
      const edgeFilterOpacity = edgeDimmed && filterMode === 'dim' ? 0.3 : 1;
      const opacity = edgeInChain && showReportingLines ? Math.min(edgeFilterOpacity, 0.55) : edgeFilterOpacity;

      map.set(edge.id, { opacity, visible });
    }

    return map;
  }, [rules, nodes, edges, matchingNodeIds, reportingChainNodeIds, reportingChainEdgeIds]);
  const nodeById = useMemo(
    () => new Map(nodes.map((node) => [node.id, node] as const)),
    [nodes],
  );
  const renderedEdges = useMemo(() => {
    const secondaryReportsLensEnabled = rules.some((rule) => (
      rule.enabled &&
      getRuleKind(rule) !== 'filter' &&
      rule.display.secondaryReportsLens?.enabled === true
    ));
    const successorLensVisibility = rules.find((rule) =>
      rule.enabled && rule.display.successorLens != null)?.display.successorLens?.visibility;
    const derivedSuccessorEdges = deriveSuccessionLensEdges({
      diagramType,
      nodes,
      edges,
      lensActive: successorLensVisibility != null,
    });
    const edgesToRender = derivedSuccessorEdges.length > 0
      ? [...edges, ...derivedSuccessorEdges]
      : edges;

    return edgesToRender.map((edge) => {
      const fromNode = nodeById.get(edge.from);
      const toNode = nodeById.get(edge.to);
      const manifest = getConnectorManifest(diagramType, edge.type as any);
      const explicit = (edge as any)?.version;
      const effectiveVersion = typeof explicit === 'number' ? explicit : VersionedComponentRegistry.getDefaultVersion(edge.type as any, 'connector');
      const edgeVersion = (versions?.get(edge.id) || 0) + effectiveVersion;
      const edgeAttrs = (edge as any)?.attrs;
      const inferredHiddenNetworkImportEdge =
        diagramType === 'network' &&
        edgeTierFilter !== 'all' &&
        isImportedNetworkEdge(edgeAttrs) &&
        (edgeAttrs?.infraEdgeKind === 'identity' ||
          edgeAttrs?.infraEdgeKind === 'governance' ||
          String(edge.type) === 'role_assumption' ||
          String(edge.type) === 'governance');
      const inferredLongNetworkImportEdge =
        diagramType === 'network' &&
        edgeTierFilter === 'architecture' &&
        isImportedNetworkEdge(edgeAttrs) &&
        !isCompassExplorerEdge(edgeAttrs) &&
        !selectedIds.has(edge.id) &&
        importedEdgeDistance(fromNode, toNode) > DEFAULT_NETWORK_IMPORT_MAX_VISIBLE_EDGE_DISTANCE;
      const explicitlyHiddenImportedEdge =
        edgeAttrs?.visible === false &&
        !(diagramType === 'network' && edgeTierFilter === 'all' && isImportedNetworkEdge(edgeAttrs));
      if (explicitlyHiddenImportedEdge || edgeAttrs?.hidden === true || inferredHiddenNetworkImportEdge) {
        return null;
      }
      if (inferredLongNetworkImportEdge) {
        return null;
      }
      const edgeOpacity = edgeAttrs?.opacity;

      // M3 edge-tier styling. The infra layout materializer tags every
      // connector with `attrs.edgeTier`. Control-plane edges (IAM,
      // KMS, logs) render at 35% opacity so they sit behind data-plane
      // traffic; when the user toggles "data only", they're dropped
      // entirely. Non-infra views don't carry edgeTier — they render
      // untouched.
      const edgeTier = edgeAttrs?.edgeTier as string | undefined;
      if (edgeTier === 'control' && edgeTierFilter === 'data-only') {
        return null;
      }
      const tierOpacityMultiplier = edgeTier === 'control' ? 0.35 : 1;

      // Declarative clip for playback/video — set by ReadOnlyCanvas when
      // a viewStateOverride is active. Pure prop-driven, no imperative refs.
      const clipRadius = edgeAttrs?.__playbackClipRadius;
      const clipCenterX = edgeAttrs?.__playbackClipCenterX;
      const clipCenterY = edgeAttrs?.__playbackClipCenterY;
      const clipFunc = (typeof clipRadius === 'number' && typeof clipCenterX === 'number' && typeof clipCenterY === 'number')
        ? (ctx: any) => {
            ctx.beginPath();
            ctx.arc(clipCenterX, clipCenterY, Math.max(0, clipRadius), 0, Math.PI * 2, false);
            ctx.closePath();
          }
        : undefined;

      const isSecondaryReportsEdge = isOrgSecondaryReportsEdge(diagramType, edge);
      const secondaryReportsExplicitlyVisible = isSecondaryReportsEdge && edgeAttrs?.visible === true;
      const hasSelectedEndpoint = selectedIds.has(edge.from) || selectedIds.has(edge.to);
      // Treat selection of the edge itself as a visibility trigger too, otherwise
      // clicking a dotted/matrix line replaces the selection with just the edge
      // id, deselects the endpoint nodes, and the visibility predicate below
      // immediately hides the very line we just selected. That makes secondary
      // edges effectively unselectable.
      const isEdgeSelected = selectedIds.has(edge.id);
      const successionLensEdge = resolveSuccessionLensEdge({
        diagramType,
        edge,
        selectedIds,
        visibility: successorLensVisibility,
      });
      if (!successionLensEdge.visible) {
        return null;
      }
      const secondaryLensColor = isSecondaryReportsEdge
        ? resolveSecondaryReportsColor({
            fromNodeId: edge.from,
            edge,
            secondaryLensEnabled: secondaryReportsLensEnabled,
          })
        : undefined;
      const normalizedVisibilityMode =
        secondaryReportsVisibilityMode ?? DEFAULT_ORG_SECONDARY_REPORTS_VISIBILITY_MODE;

      if (
        isSecondaryReportsEdge &&
        !secondaryReportsExplicitlyVisible &&
        !secondaryReportsLensEnabled &&
        (
          normalizedVisibilityMode === 'hidden' ||
          (normalizedVisibilityMode === 'selection' && !hasSelectedEndpoint && !isEdgeSelected)
        )
      ) {
        return null;
      }

      // Route secondary reports edges through EdgeConnector so curved dashed
      // rendering behavior stays consistent across editor/viewer.
      if (manifest?.konvaNative && !isSecondaryReportsEdge) {
        return (
          <Group key={edge.id} opacity={(edgeOpacity ?? 1) * tierOpacityMultiplier} clipFunc={clipFunc}>
            <KonvaNativeEdge
              manifest={manifest}
              edge={edge}
              fromNode={fromNode}
              toNode={toNode}
              selected={selectedIds.has(edge.id)}
              getShapeState={getShapeState}
              awareness={awareness}
              registerRef={(id, n) => registerRefLatest.current(id, n)}
              version={edgeVersion}
              onClick={getStableOnClick(edge.id)}
              editingEdgeText={editingEdgeText}
              labelGeometry={labelGeometry}
            />
          </Group>
        );
      }

      return (
        <Group
          key={edge.id}
          opacity={(edgeOpacity ?? 1) * tierOpacityMultiplier}
          clipFunc={clipFunc}
          listening={!edge.id.startsWith(DERIVED_SUCCESSOR_EDGE_PREFIX)}
        >
          <EdgeConnector
            edges={edgesToRender}
            nodes={nodes}
            edge={edge}
            fromNode={fromNode}
            toNode={toNode}
            selected={selectedIds.has(edge.id)}
            onClick={getStableOnClick(edge.id)}
            getShapeState={getShapeState}
            awareness={awareness}
            registerRef={getStableEdgeRef(edge.id)}
            version={edgeVersion}
            onStartTextEdit={onStartEdgeTextEdit}
            editingText={editingEdgeText === edge.id}
            editRequestEdgeId={editRequestEdgeId}
            diagramType={diagramType}
            routingPolicyId={manifest?.routingPolicyId}
            viewRuleState={edgeViewRuleStateById.get(edge.id)}
            secondaryLensColor={secondaryReportsLensEnabled ? secondaryLensColor : undefined}
            successorLensColor={successionLensEdge.color}
            canWrite={canWrite}
            labelGeometry={labelGeometry}
          />
        </Group>
      );
    });
  }, [edges, nodes, nodeById, diagramType, selectedIds, getShapeState, awareness, versions, onStartEdgeTextEdit, editingEdgeText, editRequestEdgeId, canWrite, getStableOnClick, getStableEdgeRef, edgeTierFilter, edgeViewRuleStateById, rules, secondaryReportsVisibilityMode, labelGeometry]);
  
  // Note: Edge highlighting is now handled directly by EdgeConnector via useHighlightStore
  // Each edge reads from the store and modifies its own stroke color
  
  return <>{renderedEdges}</>;
}, areEdgesRendererPropsEqual);

EdgesRenderer.displayName = 'EdgesRenderer';
